import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { GroupRoleAssignmentDto, ResponseDto } from "@mimber/schema"
import { groupRoleAssignmentsKeys, rolesKeys } from "../roles.utils"
import type { RevokeGroupRoleAssignmentParams } from "./useRevokeGroupRoleAssignment.types"

/**
 * Organization-specific hook for revoking a group role assignment
 * Can only be used in mimber-organization app
 */
export const useRevokeGroupRoleAssignment = (
  roleUuid: string,
  options?: UseMutationOptions<
    ResponseDto<GroupRoleAssignmentDto>,
    Error,
    RevokeGroupRoleAssignmentParams
  >
): UseMutationResult<
  ResponseDto<GroupRoleAssignmentDto>,
  Error,
  RevokeGroupRoleAssignmentParams
> => {
  guardAppUsage("mimber-organization", "useRevokeGroupRoleAssignment")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ assignmentUuid }: RevokeGroupRoleAssignmentParams) => {
      const response = await server.delete<ResponseDto<GroupRoleAssignmentDto>>(
        `roles/${roleUuid}/group-assignments/${assignmentUuid}`
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: groupRoleAssignmentsKeys.findByRole(roleUuid),
      })
      queryClient.invalidateQueries({
        queryKey: rolesKeys.findOne(roleUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
